import { useState, useEffect } from 'react';

/**
 * Detects if the current device is a mobile device
 * @returns {boolean} True if mobile device detected
 */
export const isMobileDevice = () => {
  if (typeof window === 'undefined') return false;
  
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
  
  return (
    mobileRegex.test(userAgent) ||
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    window.matchMedia('(pointer: coarse)').matches
  );
};

/**
 * React hook for detecting mobile devices with real-time updates
 * @returns {boolean} True if mobile device detected
 */
export const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkIsMobile = () => setIsMobile(isMobileDevice());
    
    checkIsMobile();
    
    const mediaQuery = window.matchMedia('(pointer: coarse)');
    const handleChange = () => checkIsMobile();
    
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleChange);
    }
    
    window.addEventListener('resize', handleChange);
    
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        // Fallback for older browsers
        mediaQuery.removeListener(handleChange);
      }
      window.removeEventListener('resize', handleChange);
    };
  }, []);
  
  return isMobile;
};

/**
 * Get detailed touch and pointer capabilities of the device
 * @returns {object} Object containing touch capabilities
 */
export const getTouchCapabilities = () => ({
  hasTouch: 'ontouchstart' in window,
  maxTouchPoints: navigator.maxTouchPoints || 0,
  hasCoarsePointer: window.matchMedia('(pointer: coarse)').matches,
  hasHover: window.matchMedia('(hover: hover)').matches,
  hasFinePointer: window.matchMedia('(pointer: fine)').matches,
  prefersReducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches
});

/**
 * Hook for getting real-time touch capabilities
 * @returns {object} Object containing touch capabilities
 */
export const useTouchCapabilities = () => {
  const [capabilities, setCapabilities] = useState({
    hasTouch: false,
    maxTouchPoints: 0,
    hasCoarsePointer: false,
    hasHover: false,
    hasFinePointer: false,
    prefersReducedMotion: false
  });
  
  useEffect(() => {
    const updateCapabilities = () => setCapabilities(getTouchCapabilities());
    
    updateCapabilities();
    
    // Listen for media query changes
    const mediaQueries = [
      '(pointer: coarse)',
      '(hover: hover)', 
      '(pointer: fine)',
      '(prefers-reduced-motion: reduce)'
    ];
    
    const listeners = mediaQueries.map(query => {
      const mq = window.matchMedia(query);
      const handler = updateCapabilities;
      
      if (mq.addEventListener) {
        mq.addEventListener('change', handler);
      } else {
        mq.addListener(handler);
      }
      
      return { mq, handler };
    });
    
    window.addEventListener('resize', updateCapabilities);
    
    return () => {
      listeners.forEach(({ mq, handler }) => {
        if (mq.removeEventListener) {
          mq.removeEventListener('change', handler);
        } else {
          mq.removeListener(handler);
        }
      });
      window.removeEventListener('resize', updateCapabilities);
    };
  }, []);
  
  return capabilities;
};

/**
 * Utility for conditional class names based on device type
 * @param {string} baseClass - Base CSS class
 * @param {string} hoverClass - Class to add on hover-capable devices
 * @param {string} touchClass - Class to add on touch devices
 * @returns {string} Combined class string
 */
export const getInteractionClasses = (baseClass, hoverClass = '', touchClass = '') => {
  const isMobile = isMobileDevice();
  return `${baseClass} ${isMobile ? touchClass : hoverClass}`.trim();
};

/**
 * Debounced resize handler for performance
 * @param {Function} callback - Function to call on resize
 * @param {number} delay - Debounce delay in milliseconds
 * @returns {Function} Cleanup function
 */
export const useResizeDebounced = (callback, delay = 250) => {
  useEffect(() => {
    let timeoutId;
    
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(callback, delay);
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, [callback, delay]);
}; 